import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";

import './VenueEvent.scss';

export const AdministratorVenueEventHiddenFromReports = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const defaultReport = {
        reportId: "",
        name: "Select Report",
    };

    const [venueEventHiddenFromReports, setVenueEventHiddenFromReports] = useState([]);
    const [areVenueEventHiddenFromReportsLoading, setAreVenueEventHiddenFromReportsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [selectedReport, setSelectedReport] = useState(defaultReport);
    const [reports, setReports] = useState(undefined);

    const getVenueEventHiddenFromReports = () => {
        setAreVenueEventHiddenFromReportsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
        };
    
        fetch(`/api/administrator/venueeventhiddenfromreports`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setVenueEventHiddenFromReports(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setLoadingError(error.message);
            })
            .finally(() => {
                setAreVenueEventHiddenFromReportsLoading(false);
            });
    }

    const deleteVenueEventHiddenFromReport = (venueEventHiddenFromReportKey) => {
        setAreVenueEventHiddenFromReportsLoading(true);

        const dataItem = {
            venueEventHiddenFromReportKey: venueEventHiddenFromReportKey
        }

        fetch("/api/administrator/venueeventhiddenfromreport",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getVenueEventHiddenFromReports();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventHiddenFromReportsLoading(false);
            });
    }

    const hideEventFromReport = () => {
        const reportId = selectedReport.reportId;

        if (reportId === "") {
            addNotificationWithTimeout(dispatch, "error", "You must select a report");
            return;
        }

        setAreVenueEventHiddenFromReportsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
            reportId: reportId,
        }

        fetch("/api/administrator/venueeventhiddenfromreport",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Hid event from report");

                getVenueEventHiddenFromReports();

                setSelectedReport(defaultReport);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventHiddenFromReportsLoading(false);
            });
    }

    const deleteShortAndHideFromEventsNotShorted = () => {
        if (window.confirm("Are you sure you want to delete this short and hide the event?") === false) {
            return;
        }

        setAreVenueEventHiddenFromReportsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey
        }

        fetch("/api/administrator/pos/deleteandhideevent",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted short and hid event");

                getVenueEventHiddenFromReports();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventHiddenFromReportsLoading(false);
            });
    }

    const discard = (dataItem) => {
        const newVenueEventHiddenFromReports = [...venueEventHiddenFromReports];
        newVenueEventHiddenFromReports.splice(0, 1);
        setVenueEventHiddenFromReports(newVenueEventHiddenFromReports);
    };

    const remove = (dataItem) => {
        deleteVenueEventHiddenFromReport(dataItem.venueEventHiddenFromReportKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="venueEventHiddenFromReportKey"
        />
    );

    const getReports = () => {
        fetch(`/api/general/reports`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allReports = [defaultReport].concat(result);
                setReports(allReports);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const reportChanged = (event) => {
        setSelectedReport(event.target.value);
    };

    useEffect(() => {
        if (props.venueEventKey) {
            getReports();
            getVenueEventHiddenFromReports();
        }
    }, []);

    return (
        <>            
            {areVenueEventHiddenFromReportsLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <>
                    <div className="row mb-3 mt-3">
                        <div className="col">
                            <Button
                                type={"button"}
                                onClick={deleteShortAndHideFromEventsNotShorted}
                            >
                                Delete Short and Hide
                            </Button>
                        </div>
                    </div>

                    <span className={"k-form-separator"} />

                    <div className="row mb-3 mt-3">
                        <div className="col-md-6 col-lg-8">
                            <DropDownList
                                id="report"
                                textField="name"
                                dataItemKey="reportId"
                                data={reports}
                                value={selectedReport}
                                onChange={reportChanged}
                            />
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <Button
                                themeColor={"primary"}
                                type={"button"}
                                onClick={hideEventFromReport}
                            >
                                Hide Event from Report
                            </Button>
                        </div>                        
                    </div>

                    <Grid
                        data={venueEventHiddenFromReports}
                        editField={editField}
                        dataItemKey={"venueEventHiddenFromReportKey"}
                        resizable={true}
                        scrollable="none"
                    > 
                        <Column field="reportName" title={"Report"} />
                        <Column field="loginName" title={"Login Name"} />
                        <Column field="dateCreated" title={"Date Hidden"} />
                        <Column cell={MyCommandCell} width="150px" />
                    </Grid>                    
                </>
            ) : <></>)
            }
        </>
    );
}